/**
 * Pure bounding-box computation for every marquee shape supported by the
 * image editor. Extracted from `useFabricCanvas` so it can be unit-tested
 * without spinning up a Fabric.js canvas.
 *
 * The marquee `Rect` / `Ellipse` objects are mutated in-place during drag
 * (via `set({ left, top, width, height })`) without a matching
 * `setCoords()` call, so `getBoundingRect()` returns a stale cached value
 * — typically the original 0×0 it was constructed at, which would collapse
 * cut/copy/paste output to a 1px column. Compute from intrinsic properties
 * instead.
 */

export interface Point {
  x: number;
  y: number;
}

export interface BBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type MarqueeBoundsInput =
  | { shape: 'rect'; left: number; top: number; width: number; height: number }
  | { shape: 'ellipse'; left: number; top: number; rx: number; ry: number }
  | { shape: 'lasso' | 'polygon'; points: Point[] }
  | { shape: 'wand'; polygons: Point[][] };

export function computeMarqueeBounds(m: MarqueeBoundsInput): BBox | null {
  if (m.shape === 'rect') {
    return { left: m.left, top: m.top, width: m.width, height: m.height };
  }
  if (m.shape === 'ellipse') {
    return { left: m.left, top: m.top, width: m.rx * 2, height: m.ry * 2 };
  }
  if (m.shape === 'lasso' || m.shape === 'polygon') {
    if (m.points.length < 3) return null;
    return boundsOfPoints(m.points);
  }
  if (m.shape !== 'wand') return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const poly of m.polygons) {
    for (const p of poly) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (!Number.isFinite(minX)) return null;
  return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
}

function boundsOfPoints(points: Point[]): BBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
}
